import { useEffect, useReducer } from "react";
import { Input } from "../inputs/Input";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTransactions } from "../../hooks/useTransactions";
import RenderTable from "../ui/RenderTable";
import TransactionsTable from "../../pages/transactions/TransactionsTable";
import { general } from "../../locales/general";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import Pagination from "../ui/Pagination";
import { useMutation } from "react-query";
import apiService from "../../service/apiService";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { CSpinner } from "@coreui/react";
import { apiConfig } from "../../config/apiConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWallet } from "@fortawesome/free-solid-svg-icons";

interface Props {
  userData?: User;
  invalidateQuery: () => void;
}

const validations = yup.object().shape({
  balance: yup
    .number()
    .required("Ce champ est obligatoire")
    .min(1, "Le solde doit être supérieur à 0")
    .typeError("Le solde doit être supérieur à 0"),
});

type FormInput = {
  balance: number;
};

export const BalanceTab = ({ userData, invalidateQuery }: Props) => {
  const { control, handleSubmit, reset } = useForm<FormInput>({
    resolver: yupResolver(validations),
    defaultValues: { balance: 0 },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [transactionsToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const {
    transactions,
    isfetchingTransaction: isLoading,
    queries,
    addQuery,
  } = useTransactions();

  useEffect(() => {
    if (userData?.id) {
      addQuery("userId", userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const { toastError, toastSuccess } = useToast();
  const { token } = useCookie("vToken");

  const endpoint = apiConfig.transactions.topup(userData?.id!);

  const updateProfile = useMutation((payload: FormInput) => {
    return apiService.MakePutRequest(
      endpoint,
      { amount: payload.balance },
      token,
    );
  });

  const updateProfileHandler = async (data: FormInput) => {
    try {
      await updateProfile.mutateAsync(data);
      invalidateQuery();
      reset({ balance: 0 });
      toastSuccess("Solde mis à jour");
    } catch (error) {
      toastError(general.fr.message.operationFailed);
    }
  };

  return (
    <div className="p-4 border bg-white rounded-4">
      {/* Wallet Card & Recharge Form */}
      <div className="row g-4 mb-4 align-items-stretch">
        {/* Wallet Balance Card */}
        <div className="col-md-6">
          <div
            className="p-4 rounded-4 text-white h-100 d-flex flex-column justify-content-between position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #FF7722 0%, #FFB800 100%)",
              minHeight: "180px",
            }}
          >
            <div>
              <p className="text-white text-opacity-75 fs-6 mb-1 uppercase tracking-wider fw-semibold">
                SOLDE ACTUEL
              </p>
              <h2 className="fs-1 fw-bold tracking-tight mb-0">
                {userData?.balance !== undefined
                  ? `${userData.balance.toFixed(2)} DH`
                  : "0.00 DH"}
              </h2>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <span className="fs-6 text-white text-opacity-75">
                {userData?.fullName}
              </span>
              <FontAwesomeIcon
                icon={faWallet}
                className="fs-2 text-white text-opacity-75"
              />
            </div>
          </div>
        </div>

        {/* Recharge Form Card */}
        <div className="col-md-6">
          <div className="p-4 rounded-4 border bg-light h-100 d-flex flex-column justify-content-center">
            <h5 className="fw-bold mb-3 text-dark">Recharge de compte</h5>
            <form
              onSubmit={handleSubmit(updateProfileHandler)}
              className="d-flex align-items-end gap-3 w-100"
            >
              <div className="flex-grow-1">
                <label
                  htmlFor="solde"
                  className="form-label text-sm text-secondary fw-semibold mb-1"
                >
                  Montant de la recharge (DH)
                </label>
                <Input
                  type="number"
                  className="form-control rounded-3 py-2"
                  name="balance"
                  placeholder="Ex: 100"
                  id="solde"
                  step={0.01}
                  control={control}
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="btn btn-danger rounded-3 py-2 px-4 text-white fw-bold d-flex align-items-center justify-content-center"
                  style={{ minHeight: "42px" }}
                  disabled={updateProfile.isLoading}
                >
                  {updateProfile.isLoading ? (
                    <CSpinner size="sm" />
                  ) : (
                    "Recharger"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="mt-4 pt-2">
        <h4 className="fw-bold text-dark mb-3">Historique des transactions</h4>
        <RenderTable
          loading={isLoading}
          render={() =>
            transactions?.items && transactions?.items?.length > 0 ? (
              <TransactionsTable
                transactions={transactions?.items ?? []}
                onTransactionSelect={dispatch}
                showuser={false}
              />
            ) : (
              <p className="text-center fs-5 my-2 p-4 text-secondary bg-light rounded-3">
                {general.fr.noData}
              </p>
            )
          }
        />
        {transactions?.meta && transactions.meta.totalPages > 1 && (
          <div className="mt-3">
            <Pagination
              currentPage={queries.page}
              onPageChange={(page) => {
                addQuery("page", page);
              }}
              siblingCount={1}
              totalCount={transactions.meta.totalPages}
              pageSize={10}
            />
          </div>
        )}
      </div>
    </div>
  );
};
